import { GoogleOAuthProvider } from "@react-oauth/google";
import { useMutation } from "@tanstack/react-query";
import { UseFormGetValues, UseFormRegister, useForm } from "react-hook-form";
import { Link, useLocation, useNavigation } from "react-router-dom";

import { AppConfig } from "~/AppConfig";
import { PasswordReset, Session } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import FormLabel from "~/components/login/formLabel/FormLabel";
import FormTitle from "~/components/login/formTitle/FormTitle";
import PasswordInput from "~/components/login/passwordInput/PasswordInput";
import Button from "~/components/ui/button/Button";
import FormBaseErrors from "~/components/ui/formBaseErrors/FormBaseErrors";
import Input from "~/components/ui/input/Input";
import Spinner from "~/components/ui/spinner/Spinner";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useAuth } from "~/contexts/AuthProvider";
import { EMAIL_VALIDATION_REG_EX } from "~/helpers/helpers";
import labels from "~/helpers/labels";
import { tryCatchParsed } from "~/helpers/tryCatch";
import { useFormErrors } from "~/hooks/useFormErrors";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";
import { isWhiteLabel } from "~/utils/white-label";

import styles from "./Login.module.scss";
import GoogleLoginButton from "./googleLoginButton/GoogleLoginButton";

const LoginPage = () => {
  usePageTitle(PAGE_TITLES.signIn);

  const navigation = useNavigation();
  const location = useLocation();
  const { setSession } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    setError,
  } = useForm<Session>();
  const { baseErrors, applyServerErrors } = useFormErrors(setError);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: Session) => apiClient.session.sessionCreate(data),
  });

  const isProcessing =
    isSubmitting || isPending || navigation.state === "loading";

  const onSubmit = async (data: Session) => {
    const result = await tryCatchParsed(
      mutateAsync({ email: data.email || "", password: data.password || "" }),
    );

    if (result.error) {
      applyServerErrors(result.error);

      return;
    }

    // Navigation is intentionally handled by GuestOnlyRoute (which honours
    // `location.state.from`) and the MFA gate in AppGate. The page itself
    // must not redirect based on auth state.
    setSession(result.data.data.access_token);
  };

  return (
    <>
      <FormTitle title={labels.WELCOME_BACK} />
      <FormBaseErrors errors={baseErrors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.inputsWrapper}>
          <FormLabel label={labels.EMAIL} />
          <Input
            disabled={isProcessing}
            errormessage={errors.email?.message ?? ""}
            {...register("email", {
              required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
              pattern: {
                value: EMAIL_VALIDATION_REG_EX,
                message: labels.INVALID_EMAIL,
              },
            })}
          />
        </div>
        <div className={styles.inputsWrapper}>
          <PasswordInput
            disabled={isProcessing}
            register={register as unknown as UseFormRegister<PasswordReset>}
            passwordError={errors.password?.message}
            forgotPasswordLink
            getValues={getValues as unknown as UseFormGetValues<PasswordReset>}
          />
        </div>
        <Button fullWidth type="submit" disabled={isProcessing}>
          <div className={styles.buttonSubmit}>
            {/*
             // @ts-expect-error TS2339 non-existing className */}
            <div className={styles.buttonIcon}>
              {isProcessing && <Spinner size="1rem" />}
            </div>
            {labels.LOG_IN}
          </div>
        </Button>
      </form>
      {!isWhiteLabel() && (
        <div className={styles.buttonsWrapper}>
          <div className={styles.dividerWrapper}>
            <div className={styles.divider} />
            <p className={styles.orText}>{labels.OR}</p>
            <div className={styles.divider} />
          </div>
          <GoogleOAuthProvider clientId={AppConfig.GOOGLE_AUTH_CLIENT_ID}>
            <GoogleLoginButton disabled={isPending} />
          </GoogleOAuthProvider>
          <div className={styles.footer}>
            <div className={styles.footerText}>
              {labels.DONT_HAVE_AN_ACCOUNT}
            </div>
            <Link
              className={styles.footerLink}
              to={routes.auth.signUp()}
              state={location.state}
            >
              {labels.SIGN_UP}
            </Link>
          </div>
        </div>
      )}
    </>
  );
};
export default LoginPage;
